import { BaseQueryApi } from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import { baseQuery } from 'shared/lib/redux';
import { loggerService } from 'shared/lib/logger';

import { BOOST_EM_INSIGHT_TYPE, getInsightServiceUrl } from './constants';
import { Insight, InsightFindPayload } from './types';
import {
  getFilteredBoostEmInsights,
  getRelevantBoostEmInsights,
  normalizeBoostEmInsights,
} from './lib';

export const getBoostEmInsights = async (
  classKeys: string[],
  api: BaseQueryApi,
  extraOptions: Record<string, unknown>,
) => {
  try {
    // init insights based on existing bootstrap insight configurations to be able to fetch Boost EM Insight
    const initInsightsResponse = await baseQuery<void>(getInsightServiceUrl)(
      {
        url: `/init`,
        method: 'POST',
      },
      api,
      extraOptions,
    );

    const isInsightsInitialized =
      'meta' in initInsightsResponse &&
      initInsightsResponse.meta?.response?.status === 200;

    if (!isInsightsInitialized) {
      return {
        data: [],
      };
    }

    const insightsFindPayload: InsightFindPayload = {
      insight_type: BOOST_EM_INSIGHT_TYPE,
      class_keys: classKeys,
    };

    const { data: foundBoostEmInsights } = await baseQuery<Insight[]>(
      getInsightServiceUrl,
    )(
      {
        url: `/find`,
        method: 'POST',
        body: insightsFindPayload,
      },
      api,
      extraOptions,
    );

    if (!foundBoostEmInsights?.length) {
      return {
        data: [],
      };
    }

    const insightsIds = foundBoostEmInsights.map((insight) => insight.id);

    // return any fetched Boost EM insights even if some of the requests failed
    const boostEmInsightsResponse = await Promise.allSettled(
      insightsIds.map((id) =>
        baseQuery<Insight>(getInsightServiceUrl)(
          `insight/${id}`,
          api,
          extraOptions,
        ),
      ),
    );

    const fulfilledBoostEmInsightsResponse = boostEmInsightsResponse.map(
      (response) => {
        return response.status === 'fulfilled' ? response.value.data : null;
      },
    );

    const filteredBoostEmInsights = getFilteredBoostEmInsights(
      fulfilledBoostEmInsightsResponse,
    );

    const boostEmInsights = getRelevantBoostEmInsights(filteredBoostEmInsights);

    return {
      data: normalizeBoostEmInsights(boostEmInsights),
    };
  } catch (error) {
    loggerService.logError('Boost EM insights loading error', error);

    return {
      error,
    };
  }
};
